// @system — "More in <category>" sidebar. API posts ask `GET /api/blog/:slug/related`; the
// bundled seed posts (no DB row) fall back to the static list with the same category.
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import { text } from '@/config'
import { getRelatedPosts } from '../../../../api/@system/blog.js'
import { BLOG_POSTS } from '../BlogPage/data'
import { apiPostToBlogPost, isStaticPost } from '../BlogPage/helpers'

/** @typedef {import('../BlogPage/helpers').BlogPost} BlogPost */

/**
 * @param {BlogPost} current
 * @returns {BlogPost[]}
 */
function staticRelated(current) {
  return BLOG_POSTS.filter((p) => p.category === current.category && p.id !== current.id).slice(0, 4)
}

/**
 * @typedef {object} RelatedPostsProps
 * @property {BlogPost} current
 */

/** @param {RelatedPostsProps} props */
export function RelatedPosts({ current }) {
  const isStatic = isStaticPost(current)
  const [related, setRelated] = useState(/** @type {BlogPost[]} */ (isStatic ? staticRelated(current) : []))

  useEffect(() => {
    if (isStatic) { setRelated(staticRelated(current)); return }
    let cancelled = false
    getRelatedPosts(current.slug, 4)
      .then((data) => { if (!cancelled) setRelated((data.posts ?? []).map(apiPostToBlogPost)) })
      .catch(() => { if (!cancelled) setRelated([]) })
    return () => { cancelled = true }
  }, [current, isStatic])

  if (related.length === 0) return null

  return (
    <aside className="space-y-3" data-testid="related-posts">
      <h3 className="text-sm font-semibold text-brand-text-muted uppercase tracking-wider">
        {text.blog.moreIn.replace('{category}', current.category)}
      </h3>
      <ul className="space-y-1">
        {related.map((p) => (
          <li key={p.id}>
            <Link
              to={`/blog/${p.slug}`}
              className="flex items-start gap-2 text-sm py-1.5 px-2 rounded-md hover:bg-brand-surface-hover transition-colors group"
            >
              <ChevronRight className="h-3.5 w-3.5 text-brand-text-muted group-hover:text-brand-primary flex-shrink-0 mt-0.5" aria-hidden="true" />
              <span className="group-hover:text-brand-primary line-clamp-2">{p.title}</span>
            </Link>
          </li>
        ))}
      </ul>

      <Link
        to="/blog"
        className="min-h-6 flex items-center gap-1 text-xs text-brand-text-muted hover:text-brand-primary transition-colors mt-3"
      >
        ← {text.blog.allPosts}
      </Link>
    </aside>
  )
}
